import {
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
} from '@planuze/ui';
import { usePlansContent } from '../../../content/sections/plans.js';
import type { PlanForm } from '../../../hooks/use-plan-form.js';
import { CurrencyInput } from '../currency-input.js';

/* Grupos de campos do plano público (ADR-0704), reusados pela página de criação pública e pela
   de negociado (a de negociado esconde o switch "Público no app"). Cada grupo renderiza só os
   campos — o call-site embrulha em Card/seção. Identidade e preço são separados para mapear às
   seções "Identidade" e "Preço" da página. */

interface Props {
  readonly form: PlanForm;
}

/* Tier + ordenação + lookup key + nome de exibição (imutáveis após criar — por isso só na página
   de criação; a edição não os expõe). */
export function PublicIdentityFields({ form }: Props) {
  const copy = usePlansContent();
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label htmlFor="pf-tier">{copy.common.tier}</Label>
          <Input
            id="pf-tier"
            value={form.tier}
            placeholder={copy.common.tierPlaceholder}
            onChange={(e) => form.setTier(e.target.value)}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="pf-rank">{copy.common.rank}</Label>
          <Input
            id="pf-rank"
            type="number"
            value={String(form.rank)}
            onChange={(e) => form.setRank(Number.parseInt(e.target.value, 10) || 0)}
          />
        </div>
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="pf-key">{copy.common.lookupKey}</Label>
        <Input
          id="pf-key"
          value={form.planKey}
          placeholder={copy.formFields.lookupKeyPlaceholder}
          onChange={(e) => form.setPlanKey(e.target.value)}
        />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="pf-name">{copy.formFields.displayName}</Label>
        <Input
          id="pf-name"
          value={form.displayName}
          placeholder={copy.formFields.displayNamePlaceholder}
          onChange={(e) => form.setDisplayName(e.target.value)}
        />
      </div>
    </div>
  );
}

/* Preço + intervalo + visibilidade. Em plano de org o preço é a BASE (inclui os assentos, Model B
   ADR-0653) e o rótulo muda. O switch "Público no app" (ADR-0508) só aparece fora do modo
   direcionado — plano negociado nunca é público. */
export function PublicPricingFields({ form }: Props) {
  const copy = usePlansContent();
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label htmlFor="pf-price">
            {form.isOrgPlan ? copy.formFields.basePrice : copy.formFields.price}
          </Label>
          <CurrencyInput
            id="pf-price"
            value={form.priceCents}
            currency="USD"
            onChange={form.setPriceCents}
          />
          {form.isOrgPlan && (
            <p className="text-sm text-muted-foreground">{copy.formFields.basePriceHint}</p>
          )}
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="pf-interval">{copy.common.interval}</Label>
          <Select
            value={form.interval}
            onValueChange={(v) => form.setInterval(v as 'month' | 'year')}
          >
            <SelectTrigger id="pf-interval" aria-label={copy.common.interval}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="month">{copy.common.intervalMonthly}</SelectItem>
              <SelectItem value="year">{copy.common.intervalYearly}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      {!form.isTargeted && (
        <div className="flex items-start justify-between gap-3 rounded-md border border-border p-3">
          <div className="space-y-0.5">
            <Label htmlFor="pf-public" className="text-foreground">
              {copy.common.publicToggleLabel}
            </Label>
            <p className="text-sm text-muted-foreground">{copy.common.publicToggleDescription}</p>
          </div>
          <Switch id="pf-public" checked={form.isPublic} onCheckedChange={form.setIsPublic} />
        </div>
      )}
    </div>
  );
}
